/**
 * Interest-only debt service coverage on a stated day-count basis — the
 * browser's twin of backend `app.underwriting.io_dscr`.
 *
 * The server derives and persists `dscr_io`, `dscr_io_stabilized` and
 * `dscr_hotel_after_ffe`; this copy exists so a deal the server has not yet
 * swept (and the illustrative pipeline book) shows the same figure from the same
 * inputs, with its basis spelled out. The two are pinned together by one shared
 * fixture (`backend/tests/fixtures/io_dscr_cases.json`), read by the backend
 * suite and by `ioDscr.test.ts`: a case is added once and both languages must
 * agree on it.
 *
 * Nothing here invents an input. A rate is read only where the deal states one
 * number (a floating "SOFR + 350 bps" has none); a reserve is only what was
 * entered; an unrecognised day count has no factor. See the backend module for
 * the reasoning.
 */

export const ACT_360 = 'ACT/360'
export const THIRTY_360 = '30/360'
export const ACT_365 = 'ACT/365'

export const DAY_COUNTS = [ACT_360, THIRTY_360, ACT_365] as const
export type DayCount = (typeof DAY_COUNTS)[number]

/** Applied — and recorded as a Suggestion — when a deal states no day count. */
export const DEFAULT_DAY_COUNT: DayCount = ACT_360

/** A coupon outside this band, in percentage points, is a mis-parse. */
export const RATE_BAND_POINTS: readonly [number, number] = [1, 30]

export function normalizeDayCount(value: unknown): DayCount | null {
  if (value === null || value === undefined || typeof value === 'boolean') return null
  const key = String(value).trim().toLowerCase().replace(/[\s_-]+/g, '')
  if (!key) return null
  if (['act/360', 'actual/360', 'a/360', 'act360', 'actual360', '365/360'].includes(key)) return ACT_360
  if (['30/360', '30u/360', '30e/360', '30/360us', '30/360bondbasis', '360/360'].includes(key)) return THIRTY_360
  if (['act/365', 'actual/365', 'a/365', 'act365', 'actual365', 'act/365f', 'act/365fixed', 'actual/365fixed'].includes(key)) {
    return ACT_365
  }
  return null
}

/** How a day count reads on screen: "Actual/360". */
export function dayCountLabel(code: DayCount): string {
  return code === ACT_360 ? 'Actual/360' : code === ACT_365 ? 'Actual/365' : '30/360'
}

/** Interest paid in a year as a multiple of `principal x rate`. */
export function dayCountFactor(dayCount: unknown = DEFAULT_DAY_COUNT, daysInYear = 365): number | null {
  const code = normalizeDayCount(dayCount)
  if (code === null || (daysInYear !== 365 && daysInYear !== 366)) return null
  if (code === ACT_360) return daysInYear / 360
  if (code === ACT_365) return daysInYear / 365
  return 1
}

/** A rate as a decimal fraction (0.0725), from percentage points (7.25). */
export function rateFromPoints(points: unknown): number | null {
  if (points === null || points === undefined || typeof points === 'boolean') return null
  const number = typeof points === 'number' ? points : Number(points)
  if (!Number.isFinite(number)) return null
  const [low, high] = RATE_BAND_POINTS
  return number >= low && number <= high ? number / 100 : null
}

/**
 * The deal's all-in rate as a decimal, where it states ONE number. Reads what
 * the derivation engine reads from the text `rate` field: "6.35%" and "6.35"
 * are 0.0635; "SOFR + 350 bps", a range and a rate with a floor are nothing.
 */
export function parseAllInRate(text: unknown): number | null {
  if (text === null || text === undefined || typeof text === 'boolean') return null
  if (typeof text === 'number') return rateFromPoints(text)
  const cleaned = String(text).trim().replace(/,/g, '').replace(/\$/g, '').replace(/%+$/, '').trim()
  if (!cleaned) return null
  const points = Number(cleaned)
  return Number.isFinite(points) ? rateFromPoints(points) : null
}

function positive(value: unknown): number | null {
  if (value === null || value === undefined || typeof value === 'boolean' || value === '') return null
  const number = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(number) && number > 0 ? number : null
}

/** `principal x rate x day-count factor`; null where any operand is undefined. */
export function ioAnnualDebtService(
  principal: unknown,
  annualRate: unknown,
  dayCount: unknown = DEFAULT_DAY_COUNT,
  daysInYear = 365,
): number | null {
  const amount = positive(principal)
  const rate = positive(annualRate)
  const factor = dayCountFactor(dayCount, daysInYear)
  if (amount === null || rate === null || factor === null) return null
  return amount * rate * factor
}

/** NOI over interest-only debt service; null where the service is undefined. */
export function ioDscr(noi: unknown, annualDebtService: unknown): number | null {
  if (noi === null || noi === undefined || typeof noi === 'boolean' || noi === '') return null
  const numerator = typeof noi === 'number' ? noi : Number(noi)
  const service = positive(annualDebtService)
  if (!Number.isFinite(numerator) || service === null) return null
  return numerator / service
}

/** NOI less the reserve the deal entered; null for an absent or non-positive reserve. */
export function noiAfterReserve(noi: unknown, reserve: unknown): number | null {
  const held = positive(reserve)
  if (noi === null || noi === undefined || typeof noi === 'boolean' || noi === '' || held === null) return null
  const income = typeof noi === 'number' ? noi : Number(noi)
  return Number.isFinite(income) ? income - held : null
}

export type IoCoverage = {
  dscr: number
  debtService: number
  cashFlow: number
  principal: number
  /** Decimal fraction. */
  rate: number
  dayCount: DayCount
  factor: number
}

/**
 * The whole calculation, or null when any operand is missing. `ratePoints` is
 * the catalogue's percentage points (7.25 or "7.25%"). A blank `dayCount` is the
 * platform default; a non-blank unrecognised one is null. Passing `reserve`
 * selects the hotel basis (NOI after that reserve), and a missing reserve is then
 * null — never the plain figure under a hotel label.
 */
export function ioCoverage(
  noi: unknown,
  principal: unknown,
  ratePoints: unknown,
  dayCount?: unknown,
  options: { reserve?: unknown; hotelBasis?: boolean; daysInYear?: number } = {},
): IoCoverage | null {
  const blank = dayCount === null || dayCount === undefined || String(dayCount).trim() === ''
  const code = blank ? DEFAULT_DAY_COUNT : normalizeDayCount(dayCount)
  const rate = typeof ratePoints === 'string' ? parseAllInRate(ratePoints) : rateFromPoints(ratePoints)
  if (code === null || rate === null) return null
  const service = ioAnnualDebtService(principal, rate, code, options.daysInYear ?? 365)
  const cashFlow = options.hotelBasis
    ? noiAfterReserve(noi, options.reserve)
    : typeof noi === 'number' && Number.isFinite(noi) ? noi : null
  if (service === null || cashFlow === null) return null
  const value = ioDscr(cashFlow, service)
  if (value === null) return null
  const amount = Number(principal)
  return { dscr: value, debtService: service, cashFlow, principal: amount, rate, dayCount: code, factor: service / (amount * rate) }
}
